import { useQuery } from '@tanstack/react-query'
import { useFormik } from 'formik'
import React, { useEffect, useMemo } from 'react'
import { apiUrl } from '../../../config/api'
import { customSwal } from '../../../config/swal'
import { CivilStatusId, DocumentTypeCode, RegimeTypeCode } from '../helpers/civil-regimen'
import { documentCodeToEnum, enumToDocumentCode } from '../helpers/custumerEnum'
import { getInputPropsByType } from '../helpers/inputSanitizers'

interface Props {
  data: any
  onSuccess?: () => void
}

export const EditFinalBeneficiaryForm = ({ data, onSuccess }: Props) => {
  const { data: countryData } = useQuery(
    ['country-list'],
    async () =>
      (
        await apiUrl.get('/country/list', {
          headers: { 'x-api-key': process.env.REACT_APP_PUBLIC_API_KEY },
        })
      ).data
  )
  const { data: nationalityData } = useQuery(
    ['nationality-list'],
    async () => (await apiUrl.get('/nationalities/list')).data
  )
  const { data: documentTypeData } = useQuery(
    ['document-type-list'],
    async () =>
      (
        await apiUrl.get('/document-type/list', {
          headers: { 'x-api-key': process.env.REACT_APP_PUBLIC_API_KEY },
        })
      ).data
  )
  const { data: regimeTypeData } = useQuery(
    ['regime-type-list'],
    async () => (await apiUrl.get('/regime-type/list')).data
  )
  const { data: civilStatusList } = useQuery(
    ['civil-status-beneficiary-list'],
    async () =>
      (
        await apiUrl.get('/civil-status/list', {
          params: { omit: true },
        })
      ).data
  )

  const today = new Date().toISOString().split('T')[0] // YYYY-MM-DD
  const ALLOWED_DOC_CODES = [
    DocumentTypeCode.DNI,
    DocumentTypeCode.PASAPORTE,
    DocumentTypeCode.CARNET_EXTRANJERIA,
  ] as const
  const filteredDocumentTypes =
    documentTypeData?.data?.filter(
      (d: any) => d.status === 1 && ALLOWED_DOC_CODES.includes(d.code)
    ) ?? []

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      is_final_beneficiary: data?.is_final_beneficiary || false,
      name_holder: data?.name_holder || '',
      customer_id: data?.customer_id || '',
      name_beneficiary: data?.name_beneficiary || '',
      lastname_beneficiary: data?.lastname_beneficiary || '',
      country_id: data?.country_id || '',
      date_birth: data?.date_birth?.substring(0, 10) || '',
      nationality_id: data?.nationality_id || '',
      document_type: enumToDocumentCode(data?.document_type || ''),
      document_number: data?.document_number || '',
      nit: data?.nit || '',
      ruc: data?.ruc || '',
      civil_status_id: data?.civil_status_id || '',
      name_spouse: data?.name_spouse || '',
      lastname_spouse: data?.lastname_spouse || '',
      document_type_spouse: enumToDocumentCode(data?.document_type_spouse || ''),
      document_number_spouse: data?.document_number_spouse || '',
      regime_type_id: data?.regime_type_id || '',
      date_property_regime: data?.date_property_regime?.substring(0, 10) || null,
      contact_email: data?.contact_email || '',
      contact_phone: data?.contact_phone || '',
      contact_address: data?.contact_address || '',
      contact_postal_code: data?.contact_postal_code || '',
      legal_relationship: 'Titular',
      legal_position: 'Partícipe',
      date_format: data?.date_format || '',
    },

    validate: (values: { [key: string]: any }) => {
      const errors: any = {}
      const requiredBasicFields = [
        'name_beneficiary',
        'lastname_beneficiary',
        'country_id',
        'document_number',
        'document_type',
        'nationality_id',
        'date_birth',
        'civil_status_id',
      ]

      const isSpouseRequired = ['2', '5'].includes(String(values.civil_status_id))

      requiredBasicFields.forEach((field) => {
        if (!values[field]) {
          errors[field] = 'Campo requerido'
        }
      })

      if (isSpouseRequired) {
        const extraFields = [
          'contact_email',
          'contact_phone',
          'contact_address',
          'contact_postal_code',
          'document_number_spouse',
          'document_type_spouse',
          'regime_type_id',
          'date_property_regime',
          'name_spouse',
          'lastname_spouse',
        ]

        extraFields.forEach((field) => {
          if (!values[field]) {
            errors[field] = 'Campo requerido'
          }
        })
      }

      return errors
    },

    onSubmit: async (values) => {
      try {
        const civilStatus = String(values.civil_status_id)
        const isSpouseRequired = ['2', '5'].includes(civilStatus)

        const cleanedValues = {
          ...values,
          name_spouse: isSpouseRequired ? values.name_spouse : null,
          lastname_spouse: isSpouseRequired ? values.lastname_spouse : null,
          document_type: documentCodeToEnum(values.document_type),
          document_type_spouse: isSpouseRequired
            ? documentCodeToEnum(values.document_type_spouse)
            : null,
          document_number_spouse: isSpouseRequired ? values.document_number_spouse : null,
          regime_type_id:
            isSpouseRequired && values.regime_type_id ? Number(values.regime_type_id) : null,
          date_property_regime: isSpouseRequired ? values.date_property_regime : null,
        }

        await apiUrl.patch(`/user/final-beneficiary/`, cleanedValues)

        customSwal.fire({ icon: 'success', title: 'Actualizado correctamente' }).then(() => {
          $('#EditFinalBeneficiaryModal').modal('hide')
          if (onSuccess) onSuccess()
        })
      } catch (e) {
        customSwal.fire({ icon: 'error', title: 'Error al actualizar' })
      }
    },
  })
  const isCasado = String(formik.values.civil_status_id) === String(CivilStatusId.CASADO)
  const isConviviente = String(formik.values.civil_status_id) === String(CivilStatusId.CONVIVIENTE)

  const allowedCodesByCivil: Record<string, string[]> = {
    [CivilStatusId.CASADO]: [
      RegimeTypeCode.SOCIEDAD_GANANCIALES,
      RegimeTypeCode.SEPARACION_PATRIMONIOS,
    ],
    [CivilStatusId.CONVIVIENTE]: [RegimeTypeCode.UNION_HECHO],
  }

  const filteredRegimeTypes = useMemo(() => {
    const current = String(formik.values.civil_status_id)
    const all = regimeTypeData?.data ?? []
    const codes = allowedCodesByCivil[current]
    return codes ? all.filter((r: any) => codes.includes(r.code)) : all
  }, [formik.values.civil_status_id, regimeTypeData])
  const showSpouseFields = ['2', '5'].includes(String(formik.values.civil_status_id))

  useEffect(() => {
    const all = regimeTypeData?.data ?? []
    const getIdByCodeLocal = (code: string) => all.find((i: any) => i.code === code)?.id

    if (isConviviente) {
      // Fija Unión de Hecho y no permitas cambiarlo
      const unionId = getIdByCodeLocal(RegimeTypeCode.UNION_HECHO)
      if (unionId && formik.values.regime_type_id !== unionId) {
        formik.setFieldValue('regime_type_id', unionId)
      }
    } else if (isCasado) {
      // Asegura que el valor actual sea uno permitido; si no, límpialo
      const allowed = allowedCodesByCivil[CivilStatusId.CASADO]
      const current = all.find((i: any) => i.id === formik.values.regime_type_id)?.code as
        | string
        | undefined
      if (!current || !allowed.includes(current)) {
        formik.setFieldValue('regime_type_id', '')
      }
    } else {
      // Otros estados: opcional — podrías permitir todos o limpiar
      // formik.setFieldValue('regime_type_id', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.civil_status_id, regimeTypeData])

  return (
    <form onSubmit={formik.handleSubmit} className='px-3'>
      <div className='d-flex gap-3 mb-3'>
        <div className='w-50'>
          <label className='text-muted fs-6 mb-2'>Nombres</label>
          <input
            maxLength={50}
            className={`form-control form-control-solid ${
              formik.touched.name_beneficiary && formik.errors.name_beneficiary ? 'is-invalid' : ''
            }`}
            {...formik.getFieldProps('name_beneficiary')}
            {...getInputPropsByType('textWithÑ')}
          />
          {formik.touched.name_beneficiary &&
            typeof formik.errors.name_beneficiary === 'string' && (
              <div className='invalid-feedback'>{formik.errors.name_beneficiary}</div>
            )}
        </div>
        <div className='w-50'>
          <label className='text-muted fs-6 mb-2'>Apellidos</label>

          <input
            maxLength={50}
            className={`form-control form-control-solid ${
              formik.touched.lastname_beneficiary && formik.errors.lastname_beneficiary
                ? 'is-invalid'
                : ''
            }`}
            {...formik.getFieldProps('lastname_beneficiary')}
            {...getInputPropsByType('textWithÑ')}
          />
          {formik.touched.lastname_beneficiary &&
            typeof formik.errors.lastname_beneficiary === 'string' && (
              <div className='invalid-feedback'>{formik.errors.lastname_beneficiary}</div>
            )}
        </div>
      </div>

      <div className='d-flex gap-3 mb-3'>
        <div className='w-50'>
          <label className='text-muted fs-6 mb-2'>País de residencia </label>
          <select
            className={`form-select form-control-solid ${
              formik.touched.country_id && formik.errors.country_id ? 'is-invalid' : ''
            }`}
            {...formik.getFieldProps('country_id')}
          >
            <option value=''>Seleccione</option>
            {countryData?.data.map((country: any) => (
              <option key={country.id} value={country.id}>
                {country.description}
              </option>
            ))}
          </select>
          {formik.touched.country_id && typeof formik.errors.country_id === 'string' && (
            <div className='invalid-feedback'>{formik.errors.country_id}</div>
          )}
        </div>
        <div className='w-50'>
          <label className='text-muted fs-6 mb-2'>Fecha de nacimiento</label>

          <input
            type='date'
            max={today}
            className={`form-control form-control-solid ${
              formik.touched.date_birth && formik.errors.date_birth ? 'is-invalid' : ''
            }`}
            {...formik.getFieldProps('date_birth')}
          />
          {formik.touched.date_birth && typeof formik.errors.date_birth === 'string' && (
            <div className='invalid-feedback'>{formik.errors.date_birth}</div>
          )}
        </div>
      </div>

      <div className='d-flex gap-3 mb-3'>
        <div className='w-50'>
          <label className='text-muted fs-6 mb-2'>Nacionalidad </label>
          <select
            className={`form-select form-control-solid ${
              formik.touched.nationality_id && formik.errors.nationality_id ? 'is-invalid' : ''
            }`}
            {...formik.getFieldProps('nationality_id')}
          >
            <option value=''>Seleccione</option>
            {nationalityData?.data.map((n: any) => (
              <option key={n.id} value={n.id}>
                {n.description}
              </option>
            ))}
          </select>
          {formik.touched.nationality_id && typeof formik.errors.nationality_id === 'string' && (
            <div className='invalid-feedback'>{formik.errors.nationality_id}</div>
          )}
        </div>
        <div className='w-50'>
          <label className='text-muted fs-6 mb-2'>Tipo de documento</label>
          <select
            className={`form-select form-control-solid ${
              formik.touched.document_type && formik.errors.document_type ? 'is-invalid' : ''
            }`}
            {...formik.getFieldProps('document_type')}
          >
            <option value=''>Seleccione</option>
            {filteredDocumentTypes.map((doc: any) => (
              <option key={doc.code} value={doc.code}>
                {doc.description}
              </option>
            ))}
          </select>

          {formik.touched.document_type && typeof formik.errors.document_type === 'string' && (
            <div className='invalid-feedback'>{formik.errors.document_type}</div>
          )}
        </div>
      </div>

      <div className='mb-3'>
        <label className='text-muted fs-6 mb-2'>Número de documento</label>
        <input
          maxLength={9}
          className={`form-control form-control-solid ${
            formik.touched.document_number && formik.errors.document_number ? 'is-invalid' : ''
          }`}
          {...formik.getFieldProps('document_number')}
          {...getInputPropsByType('numeric')}
        />
        {formik.touched.document_number && typeof formik.errors.document_number === 'string' && (
          <div className='invalid-feedback'>{formik.errors.document_number}</div>
        )}
        {formik.touched.document_number && Array.isArray(formik.errors.document_number) && (
          <div className='invalid-feedback'>{formik.errors.document_number.join(', ')}</div>
        )}
      </div>

      <div className='d-flex gap-3 mb-3'>
        <div className='w-50'>
          <label className='text-muted fs-6 mb-2'>NIT</label>
          <input
            maxLength={15}
            placeholder='NIT (opcional)'
            className='form-control form-control-solid'
            {...formik.getFieldProps('nit')}
            {...getInputPropsByType('alphanumeric')}
          />
        </div>
        <div className='w-50'>
          <label className='text-muted fs-6 mb-2'>RUC</label>
          <input
            maxLength={11}
            placeholder='RUC (opcional)'
            className='form-control form-control-solid'
            {...formik.getFieldProps('ruc')}
            {...getInputPropsByType('alphanumeric')}
          />
        </div>
      </div>

      <div className='mb-3'>
        <label className='text-muted fs-6 mb-2 d-block'>Estado civil</label>
        <div className='d-flex flex-wrap gap-4'>
          {civilStatusList?.data?.map((item: any) => (
            <div className='form-check form-check-custom form-check-solid' key={item.id}>
              <input
                className='form-check-input'
                type='radio'
                id={`civil-${item.id}`}
                value={item.id}
                checked={formik.values.civil_status_id === item.id}
                onChange={() => formik.setFieldValue('civil_status_id', item.id)}
              />
              <label className='form-check-label' htmlFor={`civil-${item.id}`}>
                {item.description}
              </label>
            </div>
          ))}
        </div>
      </div>

      {showSpouseFields && (
        <>
          <div className='d-flex gap-3 mb-3'>
            <div className='w-50'>
              <label className='text-muted fs-6 mb-2'>Nombre del cónyuge</label>
              <input
                maxLength={50}
                className={`form-control form-control-solid ${
                  formik.touched.name_spouse && formik.errors.name_spouse ? 'is-invalid' : ''
                }`}
                {...formik.getFieldProps('name_spouse')}
                {...getInputPropsByType('textWithÑ')}
              />
              {formik.touched.name_spouse && Array.isArray(formik.errors.name_spouse) && (
                <div className='invalid-feedback'>{formik.errors.name_spouse.join(', ')}</div>
              )}
            </div>
            <div className='w-50'>
              <label className='text-muted fs-6 mb-2'>Apellido del cónyuge</label>
              <input
                maxLength={50}
                className={`form-control form-control-solid ${
                  formik.touched.lastname_spouse && formik.errors.lastname_spouse
                    ? 'is-invalid'
                    : ''
                }`}
                {...formik.getFieldProps('lastname_spouse')}
                {...getInputPropsByType('textWithÑ')}
              />
              {formik.touched.lastname_spouse &&
                typeof formik.errors.lastname_spouse === 'string' && (
                  <div className='invalid-feedback'>{formik.errors.lastname_spouse}</div>
                )}
            </div>
          </div>

          <div className='d-flex gap-3 mb-3'>
            <div className='w-50'>
              <label className='text-muted fs-6 mb-2'>Tipo de documento del cónyuge</label>

              <select
                className={`form-select form-control-solid ${
                  formik.touched.document_type_spouse && formik.errors.document_type_spouse
                    ? 'is-invalid'
                    : ''
                }`}
                {...formik.getFieldProps('document_type_spouse')}
              >
                <option value=''>Seleccione</option>
                {filteredDocumentTypes.map((doc: any) => (
                  <option key={doc.code} value={doc.code}>
                    {doc.description}
                  </option>
                ))}
              </select>
            </div>
            <div className='w-50'>
              <label className='text-muted fs-6 mb-2'>Número documento cónyuge</label>

              <input
                maxLength={9}
                className={`form-control form-control-solid ${
                  formik.touched.document_number_spouse && formik.errors.document_number_spouse
                    ? 'is-invalid'
                    : ''
                }`}
                {...formik.getFieldProps('document_number_spouse')}
                {...getInputPropsByType('numeric')}
              />
              {formik.touched.document_number_spouse &&
                Array.isArray(formik.errors.document_number_spouse) && (
                  <div className='invalid-feedback'>
                    {formik.errors.document_number_spouse.join(', ')}
                  </div>
                )}
            </div>
          </div>

          <div className='d-flex gap-3 mb-3'>
            <div className='w-50'>
              <label className='text-muted fs-6 mb-2'>Régimen patrimonial</label>
              <select
                className={`form-select form-control-solid ${
                  formik.touched.regime_type_id && formik.errors.regime_type_id ? 'is-invalid' : ''
                }`}
                {...formik.getFieldProps('regime_type_id')}
                disabled={isConviviente}
              >
                <option value=''>Seleccione</option>
                {filteredRegimeTypes.map((reg: any) => (
                  <option key={reg.id} value={reg.id}>
                    {reg.description}
                  </option>
                ))}
              </select>
            </div>
            <div className='w-50'>
              <label className='text-muted fs-6 mb-2'>Fecha régimen</label>
              <input
                type='date'
                max={today}
                className={`form-control form-control-solid ${
                  formik.touched.date_property_regime && formik.errors.date_property_regime
                    ? 'is-invalid'
                    : ''
                }`}
                {...formik.getFieldProps('date_property_regime')}
              />{' '}
              {formik.touched.date_property_regime &&
                Array.isArray(formik.errors.date_property_regime) && (
                  <div className='invalid-feedback'>
                    {formik.errors.date_property_regime.join(', ')}
                  </div>
                )}
            </div>
          </div>
          <div className='d-flex gap-3 mb-3'>
            <div className='w-50'>
              <label className='text-muted fs-6 mb-2'>Correo electrónico</label>

              <input
                maxLength={50}
                className={`form-control form-control-solid ${
                  formik.touched.contact_email && formik.errors.contact_email ? 'is-invalid' : ''
                }`}
                {...formik.getFieldProps('contact_email')}
              />
              {formik.touched.contact_email && Array.isArray(formik.errors.contact_email) && (
                <div className='invalid-feedback'>{formik.errors.contact_email.join(', ')}</div>
              )}
            </div>
            <div className='w-50'>
              <label className='text-muted fs-6 mb-2'>Teléfono</label>
              <input
                maxLength={9}
                className={`form-control form-control-solid ${
                  formik.touched.contact_phone && formik.errors.contact_phone ? 'is-invalid' : ''
                }`}
                {...formik.getFieldProps('contact_phone')}
              />
              {formik.touched.contact_phone && Array.isArray(formik.errors.contact_phone) && (
                <div className='invalid-feedback'>{formik.errors.contact_phone.join(', ')}</div>
              )}
            </div>
          </div>

          <div className='mb-3'>
            <label className='text-muted fs-6 mb-2'>Dirección completa</label>
            <input
              maxLength={250}
              className={`form-control form-control-solid ${
                formik.touched.contact_address && formik.errors.contact_address ? 'is-invalid' : ''
              }`}
              {...formik.getFieldProps('contact_address')}
            />
            {formik.touched.contact_address && Array.isArray(formik.errors.contact_address) && (
              <div className='invalid-feedback'>{formik.errors.contact_address.join(', ')}</div>
            )}
          </div>

          <div className='mb-5'>
            <label className='text-muted fs-6 mb-2'>Código postal</label>
            <input
              maxLength={5}
              className={`form-control form-control-solid ${
                formik.touched.contact_postal_code && formik.errors.contact_postal_code
                  ? 'is-invalid'
                  : ''
              }`}
              {...formik.getFieldProps('contact_postal_code')}
              {...getInputPropsByType('numeric')}
            />
            {formik.touched.contact_postal_code &&
              Array.isArray(formik.errors.contact_postal_code) && (
                <div className='invalid-feedback'>
                  {formik.errors.contact_postal_code.join(', ')}
                </div>
              )}
          </div>
        </>
      )}

      <div className='modal-footer m-0 p-0'>
        <div className='text-end'>
          <button
            style={{ backgroundColor: '#eee', color: '#969696' }}
            type='button'
            id='cancelButton'
            className='btn btn col m-3'
            data-bs-dismiss='modal'
          >
            Cancelar
          </button>

          <button
            type='submit'
            className='btn btn col my-3 fw'
            style={{
              background: 'rgb(0, 123, 195)',
              color: 'white',
            }}
          >
            Actualizar
          </button>
        </div>
      </div>
    </form>
  )
}
